import React, { useState, useEffect } from "react";
import { Mail, CheckCircle, XCircle, RefreshCw, Users, Send, AlertCircle } from "lucide-react";
import { getAllStudents, updateStudentContactID } from "../services/StudentRecordService";
import { getAllUsers } from "../services/AxcelerateService";
import "../css/InvoiceSettings.css";

function InvoiceSettings() {
  const [students, setStudents] = useState([]);
  const [axcelerateContacts, setAxcelerateContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncingStudents, setSyncingStudents] = useState({}); // Track individual sync operations
  const [filter, setFilter] = useState("all"); // all, synced, not-synced, invoiced, not-invoiced
  const [searchTerm, setSearchTerm] = useState("");

  // Load students on mount
  useEffect(() => {
    loadStudents();
  }, []);

  const loadStudents = async () => {
    setLoading(true);
    try {
      const allStudents = await getAllStudents();
      setStudents(allStudents || []);
    } catch (error) {
      console.error("Error loading students:", error);
      alert("Failed to load students");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to normalize strings for comparison
  const normalizeString = (str) => {
    if (!str) return "";
    return str.toLowerCase().trim().replace(/\s+/g, " ");
  };

  // Helper function to normalize date to YYYY-MM-DD format
  const normalizeDate = (date) => {
    if (!date) return "";
    // If already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
    
    // Try to parse and format
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return "";
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (e) {
      return "";
    }
  };

  // Match student with Axcelerate contact
  const matchStudentWithContact = (student) => {
    const studentEmail = normalizeString(student.formState?.personal?.email || student.formState?.email);
    const studentDOB = normalizeDate(student.formState?.personal?.dateOfBirth || student.formState?.dob);
    const studentFirstName = normalizeString(student.formState?.personal?.givenNames || student.formState?.firstName);
    const studentLastName = normalizeString(student.formState?.personal?.familyName || student.formState?.lastName);
    const studentFullName = `${studentFirstName} ${studentLastName}`.trim();
    
    console.log(`Matching student ${student.id}: email="${studentEmail}", dob="${studentDOB}", name="${studentFullName}"`);
    console.log(`Against ${axcelerateContacts.length} contacts from Axcelerate`);
    
    for (const contact of axcelerateContacts) {
      const contactEmail = normalizeString(contact.EMAILADDRESS);
      const contactDOB = normalizeDate(contact.DOB);
      const contactGivenName = normalizeString(contact.GIVENNAME);
      const contactMiddleName = normalizeString(contact.MIDDLENAME);
      const contactSurname = normalizeString(contact.SURNAME);
      let contactFullName = `${contactGivenName} ${contactSurname}`.trim();
      
      if (contactMiddleName !== "") {
        contactFullName = `${contactGivenName} ${contactMiddleName} ${contactSurname}`.trim();
        console.log("middle name: ", contactMiddleName);
      }
      
      // Match criteria: email AND (DOB OR full name)
      const emailMatch = studentEmail && contactEmail && studentEmail === contactEmail;
      const dobMatch = studentDOB && contactDOB && studentDOB === contactDOB;
      const nameMatch = studentFullName && contactFullName && studentFullName === contactFullName;

      if (emailMatch) {
        return {
          matched: true,
          contactId: contact.CONTACTID,
          matchType: "email"
        };
      }
    }

    return { matched: false, contactId: null, matchType: null };
  };

  // Sync individual student
  const handleSyncIndividual = async (student) => {
    if (!axcelerateContacts.length) {
      alert("Please sync all students first to load Axcelerate contacts");
      return;
    }

    setSyncingStudents(prev => ({ ...prev, [student.id]: true }));

    try {
      const match = matchStudentWithContact(student);

      if (match.matched) {
        // Update the student's contactID in the database
        const result = await updateStudentContactID(student.id, match.contactId);

        if (result.success) {
          // Update local state
          setStudents(prevStudents =>
            prevStudents.map(s =>
              s.id === student.id
                ? {
                    ...s,
                    formState: {
                      ...s.formState,
                      contactID: match.contactId
                    },
                    axcelerateMatch: match
                  }
                : s
            )
          );

          alert(`Successfully synced student with Axcelerate contact ${match.contactId}`);
        } else {
          alert(`Failed to sync student: ${result.error}`);
        }
      } else {
        alert("No matching Axcelerate contact found for this student");
      }
    } catch (error) {
      console.error("Sync error:", error);
      alert("Failed to sync student");
    } finally {
      setSyncingStudents(prev => {
        const updated = { ...prev };
        delete updated[student.id];
        return updated;
      });
    }
  };

  // Sync all students with Axcelerate
  const handleSyncAll = async () => {
    if (!window.confirm("Are you sure you want to sync all students with Axcelerate? This may take a few minutes.")) {
      return;
    }

    setSyncing(true);
    try {
      const result = await getAllUsers();

      if (result.success) {
        setAxcelerateContacts(result.data);
        
        // Match students with contacts
        let matchedCount = 0;
        let updatedCount = 0;
        const updatedStudents = await Promise.all(
          students.map(async (student) => {
            const match = matchStudentWithContact(student);
            
            if (match.matched) {
              matchedCount++;
              
              // Update student in database if not already synced or if contact ID differs
              const existingContactId = student.formState?.contactID;
              if (existingContactId || !existingContactId || existingContactId !== match.contactId) {
                const updateResult = await updateStudentContactID(student.id, match.contactId);
                if (updateResult.success) {
                  updatedCount++;
                  console.log(`Updated student ${student.id} with contact ${match.contactId} (${match.matchType})`);
                }
              } else {
                const updateResult = await updateStudentContactID(student.id, "");
                if (updateResult.success) {
                  updatedCount--;
                  console.log(`Updated student ${student.id} with contact ${match.contactId} (${match.matchType})`);
                }
                console.log(`Student ${student.id} already synced with contact ${match.contactId}`);
              }
            }
            
            return {
              ...student,
              formState: match.matched 
                ? { ...student.formState, contactID: match.contactId }
                : student.formState,
              axcelerateMatch: match
            };
          })
        );

        setStudents(updatedStudents);
        alert(`Sync complete!\nMatched: ${matchedCount} students\nUpdated in database: ${updatedCount} students\nTotal students: ${students.length}`);
      } else {
        alert(`Failed to sync students: ${result.error}`);
      }
    } catch (error) {
      console.error("Sync error:", error);
      alert("Failed to sync students");
    } finally {
      setSyncing(false);
    }
  };

  // Get statistics
  const stats = {
    total: students.length,
    synced: students.filter(s => s.formState?.contactID || s.axcelerateMatch?.matched).length,
    notSynced: students.filter(s => !s.formState?.contactID && !s.axcelerateMatch?.matched).length,
    invoiced: students.filter(s => hasInvoicesSent(s)).length,
    notInvoiced: students.filter(s => !hasInvoicesSent(s) && (s.formState?.contactID || s.axcelerateMatch?.matched)).length,
  };

  // Check if student has any invoices sent
  function hasInvoicesSent(student) {
    const courses = student.formState?.courses || [];
    
    for (const course of courses) {
      const feesPlan = course.feesPlan;
      if (!feesPlan) continue;

      // Check initial deposit
      if (feesPlan.initialDeposit?.invoiceSent) return true;

      // Check installments
      const installments = feesPlan.installments || [];
      if (installments.some(inst => inst.invoiceSent)) return true;
    }

    return false;
  }

  // Get invoice count for a student
  function getInvoiceCount(student) {
    const courses = student.formState?.courses || [];
    let count = 0;

    for (const course of courses) {
      const feesPlan = course.feesPlan;
      if (!feesPlan) continue;

      if (feesPlan.initialDeposit?.invoiceSent) count++;
      
      const installments = feesPlan.installments || [];
      count += installments.filter(inst => inst.invoiceSent).length;
    }

    return count;
  }

  // Filter students
  const filteredStudents = students.filter(student => {
    // Search filter
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      !searchTerm ||
      student.formState?.studentId?.toLowerCase().includes(searchLower) ||
      student.formState?.firstName?.toLowerCase().includes(searchLower) ||
      student.formState?.lastName?.toLowerCase().includes(searchLower) ||
      student.formState?.email?.toLowerCase().includes(searchLower) ||
      student.formState?.personal?.givenNames?.toLowerCase().includes(searchLower) ||
      student.formState?.personal?.familyName?.toLowerCase().includes(searchLower) ||
      student.formState?.personal?.email?.toLowerCase().includes(searchLower);

    if (!matchesSearch) return false;

    // Status filter
    const isSynced = !!student.formState?.contactID || student.axcelerateMatch?.matched;
    const hasInvoices = hasInvoicesSent(student);

    switch (filter) {
      case "synced":
        return isSynced;
      case "not-synced":
        return !isSynced;
      case "invoiced":
        return hasInvoices;
      default:
        return true;
    }
  });

  return (
    <div className="invoice-settings">
      <div className="page-header">
        <div>
          <h1 className="page-title">Student Invoice Settings</h1>
          <p className="page-subtitle">Manage student sync and invoice tracking</p>
        </div>
        <button
          className="btn btn-primary"
          onClick={handleSyncAll}
          disabled={syncing}
          style={{ display: "flex", alignItems: "center", gap: "8px" }}
        >
          <RefreshCw size={16} className={syncing ? "spinning" : ""} />
          {syncing ? "Syncing..." : "Sync All with Axcelerate"}
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="cards-row">
        <div
          className={`card ${filter === "all" ? "selected" : ""}`}
          onClick={() => setFilter("all")}
        >
          <div className="card-header">
            <div className="icon-wrapper">
              <Users size={20} />
            </div>
            <h3>Total Students</h3>
          </div>
          {stats.total > 0 && (
            <div className="card-number-bubble">{stats.total}</div>
          )}
          <h4 className="subtitle">All registered students</h4>
        </div>

        <div
          className={`card ${filter === "synced" ? "selected" : ""}`}
          onClick={() => setFilter("synced")}
        >
          <div className="card-header">
            <div className="icon-wrapper">
              <CheckCircle size={20} />
            </div>
            <h3>Synced</h3>
          </div>
          {stats.synced > 0 && (
            <div className="card-number-bubble" style={{ backgroundColor: "green" }}>
              {stats.synced}
            </div>
          )}
          <h4 className="subtitle">Connected to Axcelerate</h4>
        </div>

        <div
          className={`card ${filter === "not-synced" ? "selected" : ""}`}
          onClick={() => setFilter("not-synced")}
        >
          <div className="card-header">
            <div className="icon-wrapper">
              <XCircle size={20} />
            </div>
            <h3>Not Synced</h3>
          </div>
          {stats.notSynced > 0 && (
            <div className="card-number-bubble" style={{ backgroundColor: "orange" }}>
              {stats.notSynced}
            </div>
          )}
          <h4 className="subtitle">Need to sync</h4>
        </div>

        <div
          className={`card ${filter === "invoiced" ? "selected" : ""}`}
          onClick={() => setFilter("invoiced")}
        >
          <div className="card-header">
            <div className="icon-wrapper">
              <Mail size={20} />
            </div>
            <h3>Invoiced</h3>
          </div>
          {stats.invoiced > 0 && (
            <div className="card-number-bubble" style={{ backgroundColor: "#056ec4" }}>
              {stats.invoiced}
            </div>
          )}
          <h4 className="subtitle">Invoices sent</h4>
        </div>
      </div>

      {/* Search */}
      <div style={{ marginBottom: "1rem" }}>
        <input
          type="text"
          placeholder="Search by Student ID, Name, or Email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: "100%",
            padding: "0.75rem",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            fontSize: "1rem",
          }}
        />
      </div>

      {/* Students Table */}
      <div className="table-container">
        {loading ? (
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <RefreshCw size={32} className="spinning" />
            <p>Loading students...</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Student ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>DOB</th>
                <th>Axcelerate Contact ID</th>
                <th>Sync Status</th>
                <th>Invoices Sent</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredStudents.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: "center", padding: "2rem" }}>
                    No students found
                  </td>
                </tr>
              ) : (
                filteredStudents.map((student) => {
                  const existingContactId = student.formState?.contactID;
                  const matchedContactId = student.axcelerateMatch?.contactId;
                  const isSynced = !!existingContactId || student.axcelerateMatch?.matched;
                  const invoiceCount = getInvoiceCount(student);
                  const isSyncingThis = syncingStudents[student.id];
                  
                  // Check if existing contactID matches the newly found one
                  const contactMismatch = existingContactId && matchedContactId && existingContactId !== matchedContactId;
                  
                  return (
                    <tr key={student.id}>
                      <td>{student.formState?.studentId || "-"}</td>
                      <td>
                        {`${student.formState?.personal?.givenNames || student.formState?.firstName || ""} ${student.formState?.personal?.familyName || student.formState?.lastName || ""}`.trim() || "-"}
                      </td>
                      <td>{student.formState?.personal?.email || student.formState?.email || "-"}</td>
                      <td>{normalizeDate(student.formState?.personal?.dateOfBirth || student.formState?.dob) || "-"}</td>
                      <td>
                        {existingContactId || matchedContactId || "-"}
                        {contactMismatch && (
                          <div style={{ fontSize: "0.75rem", color: "orange", marginTop: "2px" }}>
                            <AlertCircle size={12} style={{ marginRight: "2px", display: "inline" }} />
                            Conflict: {matchedContactId}
                          </div>
                        )}
                      </td>
                      <td>
                        {isSynced ? (
                          <span className="status-badge" style={{ backgroundColor: "green" }}>
                            <CheckCircle size={12} style={{ marginRight: "4px" }} />
                            Synced
                          </span>
                        ) : (
                          <span className="status-badge" style={{ backgroundColor: "orange" }}>
                            <XCircle size={12} style={{ marginRight: "4px" }} />
                            Not Synced
                          </span>
                        )}
                      </td>
                      
                      <td>
                        {invoiceCount > 0 ? (
                          <span style={{ color: "green", fontWeight: "600" }}>
                            {invoiceCount} invoice{invoiceCount !== 1 ? "s" : ""}
                          </span>
                        ) : (
                          <span style={{ color: "#999" }}>None</span>
                        )}
                      </td>
                      <td>
                        {!isSynced && (
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleSyncIndividual(student)}
                            disabled={isSyncingThis}
                            style={{ 
                              fontSize: "0.85rem", 
                              padding: "0.4rem 0.8rem",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px"
                            }}
                          >
                            <RefreshCw size={12} className={isSyncingThis ? "spinning" : ""} />
                            {isSyncingThis ? "Syncing..." : "Sync"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Summary */}
      <div style={{ marginTop: "1rem", padding: "1rem", backgroundColor: "#f9f9f9", borderRadius: "8px" }}>
        <strong>Showing {filteredStudents.length} of {students.length} students</strong>
        {axcelerateContacts.length > 0 && (
          <span style={{ marginLeft: "1rem", color: "#666" }}>
            ({axcelerateContacts.length} Axcelerate contacts loaded)
          </span>
        )}
        {filter !== "all" && (
          <button
            onClick={() => setFilter("all")}
            style={{
              marginLeft: "1rem",
              padding: "0.4rem 0.8rem",
              border: "none",
              borderRadius: "4px",
              backgroundColor: "#056ec4",
              color: "white",
              cursor: "pointer",
            }}
          >
            Go to Total Students
          </button>
        )}
      </div>
    </div>
  );
}

export default InvoiceSettings;